import { useState, useEffect } from 'react';
import { Image as ImageIcon, Video, AlertTriangle, Loader2 } from 'lucide-react';

const STATUS_STYLES = {
  draft: { bar: 'bg-[#94A3B8]', chip: 'bg-[#F1F5F9] text-[#64748B]' },
  scheduled: { bar: 'bg-blue-500', chip: 'bg-blue-50 text-blue-600' },
  publishing: { bar: 'bg-amber-500', chip: 'bg-amber-50 text-amber-600' },
  published: { bar: 'bg-emerald-500', chip: 'bg-emerald-50 text-emerald-600' },
  partial: { bar: 'bg-amber-500', chip: 'bg-amber-50 text-amber-700' },
  failed: { bar: 'bg-red-500', chip: 'bg-red-50 text-red-600' },
};

const NETWORK_INITIAL = { facebook: 'f', instagram: 'ig' };

function firstError(post) {
  for (const r of Object.values(post.results || {})) {
    if (r?.error) return r.error;
  }
  return null;
}

/**
 * Minitarjeta de publicación dentro de una celda del calendario —
 * reemplaza la píldora de conteo. Nunca dispara la creación de una nueva
 * publicación al hacer click (stopPropagation en el contenedor de celda
 * es responsabilidad del padre).
 *
 * Hotfix — la miniatura viene ya validada por el backend (nunca apunta a
 * un archivo inexistente ni a un video como <img src>), pero igual se
 * protege con onError por defensa en profundidad: si algo falla en el
 * navegador (red, caché, URL vencida), cae a un placeholder con icono —
 * nunca al icono roto nativo del navegador.
 */
export default function SocialCalendarPostCard({ post, onClick, compact = false }) {
  const [imgFailed, setImgFailed] = useState(false);
  useEffect(() => { setImgFailed(false); }, [post.thumbnail_url]);

  const style = STATUS_STYLES[post.status] || STATUS_STYLES.draft;
  const time = (() => {
    const d = post.scheduled_at || post.published_at || post.created_at;
    if (!d) return '';
    return new Date(d).toLocaleTimeString('es-MX', { hour: '2-digit', minute: '2-digit' });
  })();
  const error = firstError(post);
  const tooltip = [
    time, post.status, ...(post.target_networks || []),
    error ? `Error: ${error}` : null,
  ].filter(Boolean).join(' · ');

  const thumbSize = compact ? 'w-7 h-7' : 'w-11 h-11 sm:w-12 sm:h-12'; // ~44–48px cuando hay espacio
  const showImage = post.thumbnail_url && !imgFailed;

  return (
    <button
      type="button"
      onClick={(e) => { e.stopPropagation(); onClick?.(post); }}
      title={tooltip}
      data-testid={`social-calendar-card-${post.id}`}
      className={`group relative w-full text-left flex items-center gap-1.5 rounded-md border border-[#E2E8F0] bg-white overflow-hidden
        hover:border-[#F72585] focus:outline-none focus-visible:ring-2 focus-visible:ring-[#F72585] focus-visible:ring-offset-1
        ${compact ? 'p-1' : 'p-1.5'}`}
    >
      <span className={`absolute left-0 top-0 bottom-0 w-1 ${style.bar}`} />
      <span className={`${thumbSize} rounded-md flex-shrink-0 overflow-hidden bg-[#F1F5F9] flex items-center justify-center ml-1`}>
        {showImage ? (
          <img
            src={post.thumbnail_url}
            alt=""
            className="w-full h-full object-cover rounded-md"
            onError={() => setImgFailed(true)}
          />
        ) : post.media_is_video ? (
          <Video className="w-3.5 h-3.5 text-[#94A3B8]" />
        ) : (
          <ImageIcon className="w-3.5 h-3.5 text-[#94A3B8]" />
        )}
      </span>
      <span className="min-w-0 flex-1">
        <span className="flex items-center gap-1">
          <span className="text-[10px] font-medium text-[#0F172A]">{time}</span>
          {post.status === 'publishing' && <Loader2 className="w-2.5 h-2.5 animate-spin text-amber-500" />}
          {(post.status === 'failed' || post.status === 'partial') && <AlertTriangle className="w-2.5 h-2.5 text-red-500" />}
        </span>
        {!compact && (
          <span className="block text-[10px] text-[#64748B] line-clamp-2 leading-tight">
            {post.content_text || '(sin texto)'}
          </span>
        )}
      </span>
      <span className="flex flex-col gap-0.5 flex-shrink-0 mr-0.5">
        {(post.target_networks || []).slice(0, 2).map((n) => (
          <span key={n} className={`text-[8px] font-bold px-1 rounded ${style.chip}`}>{NETWORK_INITIAL[n] || n[0]}</span>
        ))}
      </span>
    </button>
  );
}
